import { useEffect, useRef } from "react";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X } from "lucide-react";
import { useMagicMomentStore } from "./use-magic-moment-store";
import { useUpdateMagicMomentState } from "@/lib/api/mentorships";

interface Day2ModalProps {
  mentorshipId: string;
  teenId?: string | null;
  conversationId?: string | null;
  open: boolean;
  onDismiss: (method: "cta" | "x_button") => void;
}

export function Day2Modal({
  mentorshipId,
  teenId,
  conversationId,
  open,
  onDismiss,
}: Day2ModalProps) {
  const { track } = useTrackEvent();
  const viewTracked = useRef(false);
  const dismissModal = useMagicMomentStore((s) => s.dismissModal);
  const { mutate: updateMagicMomentState } = useUpdateMagicMomentState();

  useEffect(() => {
    if (open && !viewTracked.current) {
      viewTracked.current = true;
      track(PostHogEvents.DAY2_MODAL_VIEW, {
        teen_id: teenId,
        conversation_id: conversationId,
      });
    }
  }, [open]);

  const handleDismiss = (method: "cta" | "x_button") => {
    track(PostHogEvents.DAY2_MODAL_DISMISS, {
      teen_id: teenId,
      dismiss_method: method,
    });
    dismissModal(mentorshipId);
    updateMagicMomentState({
      mentorshipId,
      fields: { day2_modal_dismissed: true },
    });
    onDismiss(method);
  };

  return (
    <Dialog open={open}>
      <DialogContent
        hideCloseButton
        className="w-[416px] gap-6 overflow-clip rounded-[16px] border-0 bg-white p-0 shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)]"
      >
        <div className="relative w-full px-8 pt-8">
          <div
            className="absolute -left-[17px] -top-[43px] h-[147px] w-[147px] bg-[#ff5001]"
            style={{
              maskImage: "url('/magic-moment-illustration.png')",
              WebkitMaskImage: "url('/magic-moment-illustration.png')",
              maskSize: "cover",
              WebkitMaskSize: "cover",
              maskRepeat: "no-repeat",
              WebkitMaskRepeat: "no-repeat",
            }}
          />
          <p className="text-center text-[24px] font-bold leading-[1.15] tracking-[-0.24px] text-[#2a2a28]">
            It&apos;s Day 2!
          </p>
        </div>
        <div className="flex flex-col gap-3 px-6 text-center">
          <p className="mt-4 text-[16px] leading-[1.4] text-[#1f1e1d]">
            This is a huge moment — show up with warmth, curiosity, and
            humanness.
          </p>
          <p className="text-[14px] leading-[1.4] text-[#58534c]">
            The tools above the chat bar are there to help you send something
            thoughtful.
          </p>
        </div>
        <div className="pb-4">
          <Button
            variant="ghost"
            onClick={() => handleDismiss("cta")}
            className="mx-auto flex h-[53px] w-[345px] items-center justify-center rounded-full px-6 py-4 text-[16px] font-bold leading-[1.1] tracking-[-0.16px] text-[#1f1e1d] hover:bg-[#f7f5f1]"
          >
            Got it - I&apos;m in
          </Button>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => handleDismiss("x_button")}
          className="absolute right-4 top-4 h-9 w-9 rounded-[10px] pt-2 text-[#58534c] hover:bg-transparent hover:text-[#1f1e1d]"
        >
          <X className="h-5 w-5" />
        </Button>
      </DialogContent>
    </Dialog>
  );
}
